import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { flightService } from '../../services/flightService';
import bookingService from '../../services/bookingService';
import toast from 'react-hot-toast';
import { navigateWithBookingData, mergeBookingData, serializeBookingData } from '../../utils/bookingQueryParams';
import { useSavedPassengers } from '../../hooks/useSavedPassengers';

const AddPassengersPage = ({ onNext, onBack, id: propId, selectedFlights, routeType: propRouteType }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const location = useLocation();

  const [flightData, setFlightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [bookingOnBehalfOf, setBookingOnBehalfOf] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Use saved passengers hook
  const { savedPassengers, loading: loadingSavedPassengers } = useSavedPassengers();

  // Merge data from URL params, location state, and session storage
  const bookingData = mergeBookingData(location);
  const totalPassengers = bookingData.passengers || 1;
  const routeType = propRouteType || bookingData.routeType || 'One Way';
  const pricing = bookingData.pricing || location.state?.flightData?.pricing || null;

  // --- NEW: RECOVERY/REVOCATION LOGIC ---
  useEffect(() => {
    const checkAndRevokeAbandonedLock = async () => {
      const token = localStorage.getItem('pending_booking_token');
      const timestamp = localStorage.getItem('pending_booking_time');

      if (token) {
        // If it's less than 10 minutes old, it's an active lock we should release
        // since the user is back on the passengers page.
        const timeDiff = Date.now() - parseInt(timestamp || '0');
        const isRecentlyLocked = timeDiff < 10 * 60 * 1000;

        if (isRecentlyLocked) {
          console.log('🧹 [Frontend] Found abandoned booking token on mount, revoking:', token);
          try {
            await bookingService.revokeLock(token);
            console.log('✅ [Frontend] Abandoned seat lock revoked successfully');
          } catch (err) {
            console.warn('⚠️ [Frontend] Failed to revoke abandoned lock:', err);
          }
        }

        // Always clear from localStorage once we've tried to revoke (or if old)
        localStorage.removeItem('pending_booking_token');
        localStorage.removeItem('pending_booking_time');
      }
    };

    checkAndRevokeAbandonedLock();
  }, []);
  // ----------------------------------------

  useEffect(() => {
    // For multi-city flights with selectedFlights prop, skip API call
    if (selectedFlights && selectedFlights.length > 0) {
      setFlightData(selectedFlights[0]); // Use first flight for basic info
      setLoading(false);
    } else {
      fetchFlightDetails();
    }
    fetchNationalities();
    fetchCountries();
    initializePassengers();
  }, [id, selectedFlights]); // Added selectedFlights to dependencies

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1'}/utility/countries`);
      const data = await response.json();
      if (data.success) {
        setCountries(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const fetchNationalities = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1'}/utility/nationalities`);
      const data = await response.json();
      if (data.success) {
        setNationalities(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch nationalities:', err);
    }
  };

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const response = await flightService.getFlightDetails(id);

      if (response.success) {
        setFlightData(response.data);
      } else {
        setError('Flight not found');
      }
    } catch (err) {
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const initializePassengers = () => {
    // Only use passenger data if we're coming back from payment page (has passengerData in bookingData)
    // Don't use it if we're starting fresh from flight selection
    const hasExistingPassengerData = bookingData.passengerData &&
      Array.isArray(bookingData.passengerData) &&
      bookingData.passengerData.length > 0;

    // Check if this is a fresh booking (coming from flight selection)
    // If there's no passengerData or if the passenger count changed, initialize fresh
    const isFreshBooking = !hasExistingPassengerData ||
      (hasExistingPassengerData && bookingData.passengerData.length !== totalPassengers);

    if (hasExistingPassengerData && !isFreshBooking) {
      // Returning from payment page with existing data
      setPassengers(bookingData.passengerData);
      return;
    }

    // Fresh booking - initialize empty passenger forms
    const initialPassengers = [];
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    for (let i = 0; i < totalPassengers; i++) {
      if (i === 0 && !bookingOnBehalfOf) {
        // First passenger - pre-fill with logged-in user details
        initialPassengers.push({
          gender: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          contact_number: user.mobile_number || '',
          country_code: '+91',
          date_of_birth: user.date_of_birth || '',
          nationality_id: user.nationality || ''
        });
      } else {
        // Other passengers - empty fields
        initialPassengers.push({
          gender: '',
          first_name: '',
          last_name: '',
          email: i === 0 ? '' : '',
          contact_number: i === 0 ? '' : '',
          country_code: i === 0 ? '+91' : '',
          date_of_birth: '',
          nationality_id: ''
        });
      }
    }
    setPassengers(initialPassengers);
  };

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
  };

  const handleSelectSavedPassenger = (index, passengerId) => {
    console.log('🔍 handleSelectSavedPassenger called:', { index, passengerId, savedPassengersCount: savedPassengers.length });

    if (!passengerId) {
      console.log('❌ No passengerId provided');
      return;
    }

    // Parse passengerId as it might be a string
    const parsedId = typeof passengerId === 'string' ? parseInt(passengerId) : passengerId;
    console.log('🔍 Looking for passenger with id:', parsedId);
    console.log('🔍 Available saved passengers:', savedPassengers.map(p => ({ id: p.id, type: typeof p.id, name: `${p.first_name} ${p.last_name}` })));

    const savedPassenger = savedPassengers.find(p => {
      // Handle both string and number IDs
      const savedId = typeof p.id === 'string' ? parseInt(p.id) : p.id;
      return savedId === parsedId;
    });

    if (!savedPassenger) {
      console.log('❌ Saved passenger not found');
      return;
    }

    console.log('✅ Found saved passenger:', savedPassenger);

    // Format date of birth to YYYY-MM-DD format for date input
    let formattedDOB = '';
    if (savedPassenger.date_of_birth) {
      const dobDate = new Date(savedPassenger.date_of_birth);
      formattedDOB = dobDate.toISOString().split('T')[0];
    }

    const updatedPassengers = [...passengers];
    updatedPassengers[index] = {
      ...updatedPassengers[index],
      first_name: savedPassenger.first_name || '',
      last_name: savedPassenger.last_name || '',
      email: savedPassenger.email || '',
      contact_number: savedPassenger.mobile_number || '',
      date_of_birth: formattedDOB,
      gender: savedPassenger.gender || '',
      nationality_id: savedPassenger.nationality_id || '',
      country_id: savedPassenger.country_id || ''
    };

    console.log('📝 Updated passenger data:', updatedPassengers[index]);
    setPassengers(updatedPassengers);
    toast.success('Passenger details loaded');
  };

  const handleGoBack = () => {
    // If inside FlightBookingFlow, call parent onBack to go to previous step
    if (onBack && typeof onBack === 'function') {
      onBack();
      return;
    }

    // Standalone page — navigate back to flight selection
    const flightSelectionPath = routeType === 'Returns' ? '/return-flight' :
      routeType === 'Multicity' ? '/multi-city-search' :
        '/one-way-flight';

    navigateWithBookingData(
      navigate,
      flightSelectionPath,
      {
        ...bookingData,
        passengerData: passengers
      }
    );
  };

  const validateForm = () => {
    console.log('🔍 Validating form...', {
      passengerCount: passengers.length,
      bookingOnBehalfOf
    });

    const newErrors = {};

    for (let i = 0; i < passengers.length; i++) {
      const passenger = passengers[i];
      console.log(`Validating passenger ${i + 1}:`, {
        gender: passenger.gender,
        first_name: passenger.first_name,
        last_name: passenger.last_name,
        email: passenger.email,
        contact_number: passenger.contact_number,
        date_of_birth: passenger.date_of_birth,
        nationality_id: passenger.nationality_id
      });

      if (!passenger.gender) {
        console.log(`❌ Passenger ${i + 1}: Missing gender`);
        newErrors[`gender_${i}`] = 'Please select gender';
      }

      if (!passenger.first_name || passenger.first_name.trim() === '') {
        console.log(`❌ Passenger ${i + 1}: Missing first name`);
        newErrors[`first_name_${i}`] = 'First name is required';
      }

      if (!passenger.last_name || passenger.last_name.trim() === '') {
        console.log(`❌ Passenger ${i + 1}: Missing last name`);
        newErrors[`last_name_${i}`] = 'Last name is required';
      }

      // Only validate email and contact for first passenger when NOT booking on behalf
      if (i === 0 && !bookingOnBehalfOf) {
        if (!passenger.email || passenger.email.trim() === '') {
          console.log(`❌ Passenger ${i + 1}: Missing email`);
          newErrors[`email_${i}`] = 'Email is required';
        }

        if (!passenger.contact_number || passenger.contact_number.trim() === '') {
          console.log(`❌ Passenger ${i + 1}: Missing contact number`);
          newErrors[`contact_number_${i}`] = 'Contact number is required';
        }
      }

      if (!passenger.date_of_birth) {
        console.log(`❌ Passenger ${i + 1}: Missing date of birth`);
        newErrors[`date_of_birth_${i}`] = 'Date of birth is required';
      }

      // Only validate nationality for the first passenger when NOT booking on behalf
      if (i === 0 && !bookingOnBehalfOf) {
        if (!passenger.nationality_id) {
          console.log(`❌ Passenger ${i + 1}: Missing nationality`);
          newErrors[`nationality_id_${i}`] = 'Nationality is required';
        }
      }
    }

    // Check for duplicate passengers (same first name, last name, and date of birth)
    for (let i = 0; i < passengers.length; i++) {
      for (let j = i + 1; j < passengers.length; j++) {
        const passenger1 = passengers[i];
        const passenger2 = passengers[j];

        const sameFirstName = passenger1.first_name.toUpperCase().trim() === passenger2.first_name.toUpperCase().trim();
        const sameLastName = passenger1.last_name.toUpperCase().trim() === passenger2.last_name.toUpperCase().trim();
        const sameDOB = passenger1.date_of_birth === passenger2.date_of_birth;

        if (sameFirstName && sameLastName && sameDOB) {
          console.log(`❌ Duplicate passenger detected: Passenger ${i + 1} and Passenger ${j + 1}`);
          toast.error(`Passenger ${i + 1} and Passenger ${j + 1} have the same name and date of birth. Each passenger must be unique.`);
          setValidationErrors(newErrors);
          return false;
        }
      }
    }

    setValidationErrors(newErrors);
    console.log('✅ All validations passed');
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🚀 Form submitted, validating...', {
      passengerCount: passengers.length,
      totalPassengers,
      bookingOnBehalfOf
    });

    if (!validateForm()) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('✅ Validation passed, checking for duplicate bookings...');
    setSubmitting(true);

    try {
      // Prepare flight data for duplicate check
      const flightsToCheck = [];

      if (selectedFlights && selectedFlights.length > 0) {
        // Multi-city or return flights
        selectedFlights.forEach(flight => {
          flightsToCheck.push({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport || flight.departure?.airport_id,
            arrival_airport: flight.arrival_airport || flight.arrival?.airport_id,
            departure_date_time: flight.departure_date_time || flight.departure_datetime
          });
        });
      } else if (flightData) {
        // Single flight
        flightsToCheck.push({
          flight_number: flightData.flight_number,
          departure_airport: flightData.departure_airport || flightData.departure?.airport_id,
          arrival_airport: flightData.arrival_airport || flightData.arrival?.airport_id,
          departure_date_time: flightData.departure_date_time || flightData.departure_datetime
        });
      }

      // Prepare passenger data for duplicate check
      const passengersToCheck = passengers.map(p => ({
        first_name: p.first_name.toUpperCase(),
        last_name: p.last_name.toUpperCase(),
        date_of_birth: p.date_of_birth
      }));

      console.log('🔍 Checking duplicates for:', {
        passengers: passengersToCheck,
        flights: flightsToCheck
      });

      // Check for duplicate bookings
      try {
        const duplicateCheckResponse = await bookingService.checkDuplicateBooking(
          passengersToCheck,
          flightsToCheck
        );

        if (!duplicateCheckResponse.success) {
          console.log('❌ Duplicate booking detected');
          toast.error('One or more passengers already have a booking for this flight');
          setSubmitting(false);
          return;
        }

        console.log('✅ No duplicate bookings found');
      } catch (duplicateError) {
        // ... (existing duplicate error handling)
        if (duplicateError.response?.status === 409) {
          // Duplicate found
          const duplicates = duplicateError.response?.data?.duplicates || [];
          if (duplicates.length > 0) {
            const duplicateMessages = duplicates.map(d =>
              `${d.passenger} already has a booking for flight ${d.flight} (${d.route})`
            ).join('\n');
            toast.error(`Duplicate booking detected:\n${duplicateMessages}`, { duration: 6000 });
          } else {
            toast.error('One or more passengers already have a booking for this flight');
          }
          setSubmitting(false);
          return;
        } else {
          console.warn('⚠️ Duplicate check failed, continuing anyway:', duplicateError);
        }
      }

      // --- DEFERRED: SEAT LOCKING moved to PaymentPage ---
      const bookingToken = bookingData.booking_token || null;
      // ---------------------------------------------------

      // Prepare booking data with passenger info
      const updatedBookingData = {
        ...bookingData,
        passengerData: passengers,
        passengers: totalPassengers,
        routeType,
        farePerPax: pricingData.farePerPax,
        totalFare: pricingData.totalFare,
        blockFare: pricingData.blockFare,
        amount: pricingData.isSeatPricing ? pricingData.totalFare : pricingData.blockFare,
        saveForFuture,
        booking_token: bookingToken, // Add the lock token
        // Pass selectedFlights if available (for return/multi-city)
        selectedFlights: bookingData.selectedFlights || undefined
      };

      // If onNext callback is provided (inside FlightBookingFlow), call it immediately
      if (onNext && typeof onNext === 'function') {
        console.log('📞 Calling onNext callback (inside FlightBookingFlow)...');
        // Call onNext synchronously to update parent state immediately
        onNext({
          passengers,
          totalPassengers,
          routeType,
          pricing: pricingData,
          saveForFuture,
          booking_token: bookingToken
        });
        setSubmitting(false);
        // Don't navigate - let FlightBookingFlow handle the step change
        return;
      }

      // Use navigate with absolute path - build URL manually to avoid any relative path issues
      const paymentPath = `/payment/${id}`;
      const queryString = serializeBookingData(updatedBookingData);
      const fullUrl = queryString ? `${paymentPath}?${queryString}` : paymentPath;

      // Use replace: false to add to history, not replace current entry
      navigate(fullUrl, {
        replace: false,
        state: {
          flightData,
          totalPassengers,
          routeType,
          pricing: pricingData,
          saveForFuture,
          booking_token: bookingToken,
          selectedFlights: bookingData.selectedFlights || undefined
        }
      });

      console.log('✅ Navigation initiated to:', fullUrl);
      setSubmitting(false);
    } catch (err) {
      console.error('❌ Form submission error:', err);
      toast.error('Failed to proceed. Please try again.');
      setSubmitting(false);
    }
  };

  const calculatePricing = () => {
    if (!flightData) return { farePerPax: 0, totalFare: 0, blockFare: 0, isSeatPricing: false };

    if (pricing) {
      return pricing;
    }

    let farePerPax = 0;
    let totalFare = 0;
    let blockFare = 0;
    let isSeatPricing = false;

    if (flightData.block_price && flightData.seat_price) {
      isSeatPricing = true;
      farePerPax = parseFloat(flightData.seat_price);

      if (flightData.commission_fee) {
        farePerPax = Math.round(farePerPax + (farePerPax * flightData.commission_fee / 100));
      } else {
        farePerPax = Math.round(farePerPax);
      }

      totalFare = farePerPax * totalPassengers;
    } else {
      blockFare = parseFloat(flightData.block_empty_price || 0);

      if (flightData.commission_fee) {
        blockFare = Math.round(blockFare + (blockFare * flightData.commission_fee / 100));
      } else {
        blockFare = Math.round(blockFare);
      }
    }

    return { farePerPax, totalFare, blockFare, isSeatPricing };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." className="w-24 h-24" />
      </div>
    );
  }

  if (error || !flightData) {
    return (
      <div className="min-h-screen pt-[70px]">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Flight not found'}
          </div>
        </div>
      </div>
    );
  }

  const pricingData = calculatePricing();

  return (
    <div className="min-h-screen pt-[70px]">
      {/* Main Content */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded shadow-sm">
            <div className="p-6">
              <div className="flex flex-wrap -mx-4">
                {/* Left Column - Passenger Forms */}
                <div className="w-full lg:w-2/3 px-4">
                  {/* Header Card */}
                  <div className="bg-white border-0 rounded-none mb-4">
                    <div className="bg-[#ffd200] px-4 py-3">
                      <h5 className="text-base font-bold m-0">Passenger(s) Information</h5>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="px-3">
                    {passengers.map((passenger, index) => (
                      <div key={index} className="mb-6">
                        <h5 className="text-base font-bold mb-3">Passenger {index + 1}</h5>

                        {/* Checkbox - Only for first passenger */}
                        {index === 0 && (
                          <div className="mb-4 flex items-center">
                            <input
                              type="checkbox"
                              id="booking_on_behalf"
                              checked={bookingOnBehalfOf}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setBookingOnBehalfOf(isChecked);

                                // When checking "booking on behalf", clear email/contact/nationality for first passenger
                                // When unchecking, restore user's details
                                if (passengers.length > 0) {
                                  const updatedPassengers = [...passengers];
                                  const user = JSON.parse(localStorage.getItem('user') || '{}');

                                  if (isChecked) {
                                    // Clear email, contact, and nationality for first passenger
                                    updatedPassengers[0] = {
                                      ...updatedPassengers[0],
                                      email: '',
                                      contact_number: '',
                                      country_code: '+91',
                                      nationality_id: ''
                                    };
                                  } else {
                                    // Restore user's details for first passenger
                                    updatedPassengers[0] = {
                                      ...updatedPassengers[0],
                                      email: user.email || '',
                                      contact_number: user.mobile_number || '',
                                      country_code: '+91',
                                      nationality_id: user.nationality || ''
                                    };
                                  }

                                  setPassengers(updatedPassengers);
                                }
                              }}
                              className="mr-2 w-4 h-4 cursor-pointer"
                            />
                            <label htmlFor="booking_on_behalf" className="text-sm cursor-pointer flex items-center">
                              Are you booking on behalf of someone else?
                            </label>
                          </div>
                        )}

                        {/* Saved Passengers Dropdown */}
                        {savedPassengers.length > 0 && (
                          <>
                            {/* For first passenger: only show when booking on behalf */}
                            {index === 0 && bookingOnBehalfOf && (
                              <div className="mb-4">
                                <select
                                  className="w-full md:w-1/2 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  onChange={(e) => handleSelectSavedPassenger(index, e.target.value)}
                                  defaultValue=""
                                >
                                  <option value="">Select a passenger</option>
                                  {savedPassengers.map((saved) => (
                                    <option key={saved.id} value={saved.id}>
                                      {saved.first_name} {saved.last_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {/* For other passengers: always show */}
                            {index > 0 && (
                              <div className="mb-4">
                                <select
                                  className="w-full md:w-1/2 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  onChange={(e) => handleSelectSavedPassenger(index, e.target.value)}
                                  defaultValue=""
                                >
                                  <option value="">Select a passenger</option>
                                  {savedPassengers.map((saved) => (
                                    <option key={saved.id} value={saved.id}>
                                      {saved.first_name} {saved.last_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}

                        {/* Gender Selection */}
                        <div className="w-full md:w-1/2 px-0 pr-3 mb-3">
                          <select
                            name={`gender_${index}`}
                            value={passenger.gender}
                            onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                            className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Gender</option>
                            <option value="Mister">Male</option>
                            <option value="Miss">Female</option>
                          </select>
                          {validationErrors[`gender_${index}`] && (
                            <p className="text-red-500 text-sm mt-1">{validationErrors[`gender_${index}`]}</p>
                          )}
                        </div>

                        {/* Name Fields */}
                        <div className="flex flex-wrap -mx-2 mb-3">
                          <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                            <input
                              type="text"
                              name={`first_name_${index}`}
                              placeholder="FIRST NAME"
                              value={passenger.first_name}
                              onChange={(e) => handlePassengerChange(index, 'first_name', e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                              style={{ textTransform: 'uppercase' }}
                            />
                            {validationErrors[`first_name_${index}`] && (
                              <p className="text-red-500 text-sm mt-1">{validationErrors[`first_name_${index}`]}</p>
                            )}
                          </div>
                          <div className="w-full md:w-1/2 px-2">
                            <input
                              type="text"
                              name={`last_name_${index}`}
                              placeholder="LAST NAME"
                              value={passenger.last_name}
                              onChange={(e) => handlePassengerChange(index, 'last_name', e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                              style={{ textTransform: 'uppercase' }}
                            />
                            {validationErrors[`last_name_${index}`] && (
                              <p className="text-red-500 text-sm mt-1">{validationErrors[`last_name_${index}`]}</p>
                            )}
                          </div>
                        </div>

                        {/* Conditional Fields - Show only if NOT booking on behalf OR if not first passenger */}
                        {(!bookingOnBehalfOf || index !== 0) && (
                          <>
                            {/* Email and Contact for first passenger */}
                            {index === 0 && (
                              <div className="flex flex-wrap -mx-2 mb-3">
                                <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                                  <input
                                    type="email"
                                    name={`email_${index}`}
                                    placeholder="USER@JETSCANNER360.COM"
                                    value={passenger.email}
                                    onChange={(e) => handlePassengerChange(index, 'email', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                                    style={{ textTransform: 'uppercase' }}
                                  />
                                  {validationErrors[`email_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`email_${index}`]}</p>
                                  )}
                                </div>
                                <div className="w-full md:w-1/2 px-2">
                                  <div className="flex gap-2">
                                    <select
                                      name={`country_code_${index}`}
                                      value={passenger.country_code}
                                      onChange={(e) => handlePassengerChange(index, 'country_code', e.target.value)}
                                      className="w-32 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    >
                                      <option value="+91">India +91</option>
                                      {countries.map((country) => (
                                        <option key={country.id} value={country.phone_code}>
                                          {country.name} {country.phone_code}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="tel"
                                      name={`contact_number_${index}`}
                                      placeholder="CONTACT NUMBER"
                                      value={passenger.contact_number}
                                      onChange={(e) => handlePassengerChange(index, 'contact_number', e.target.value)}
                                      className="flex-1 px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400"
                                    />
                                  </div>
                                  {validationErrors[`contact_number_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`contact_number_${index}`]}</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Date of Birth and Nationality - Only Nationality for Passenger 1 */}
                            <div className="flex flex-wrap -mx-2 mb-3">
                              <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
                                <input
                                  type="text"
                                  name={`date_of_birth_${index}`}
                                  placeholder="DATE OF BIRTH"
                                  value={passenger.date_of_birth}
                                  onFocus={(e) => {
                                    e.target.type = 'date';
                                    try { e.target.showPicker(); } catch (err) {}
                                  }}
                                  onClick={(e) => {
                                    try { e.target.showPicker(); } catch (err) {}
                                  }}
                                  onBlur={(e) => {
                                    if (!e.target.value) e.target.type = 'text';
                                  }}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => handlePassengerChange(index, 'date_of_birth', e.target.value)}
                                  className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden"
                                />
                                {validationErrors[`date_of_birth_${index}`] && (
                                  <p className="text-red-500 text-sm mt-1">{validationErrors[`date_of_birth_${index}`]}</p>
                                )}
                              </div>
                              {index === 0 && (
                                <div className="w-full md:w-1/2 px-2">
                                  <select
                                    name={`nationality_id_${index}`}
                                    value={passenger.nationality_id}
                                    onChange={(e) => handlePassengerChange(index, 'nationality_id', e.target.value)}
                                    className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="">Nationality</option>
                                    {nationalities.map((nationality) => (
                                      <option key={nationality.id} value={nationality.id}>
                                        {nationality.nationality}
                                      </option>
                                    ))}
                                  </select>
                                  {validationErrors[`nationality_id_${index}`] && (
                                    <p className="text-red-500 text-sm mt-1">{validationErrors[`nationality_id_${index}`]}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Simplified Fields - Show only if booking on behalf AND first passenger */}
                        {bookingOnBehalfOf && index === 0 && (
                          <>
                            {/* Date of Birth - Full Width */}
                            <div className="w-full md:w-1/2 px-0 pr-3 mb-3">
                              <input
                                type="text"
                                name={`date_of_birth_${index}`}
                                placeholder="DATE OF BIRTH"
                                value={passenger.date_of_birth}
                                onFocus={(e) => {
                                  e.target.type = 'date';
                                  try { e.target.showPicker(); } catch (err) {}
                                }}
                                onClick={(e) => {
                                  try { e.target.showPicker(); } catch (err) {}
                                }}
                                onBlur={(e) => {
                                  if (!e.target.value) e.target.type = 'text';
                                }}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => handlePassengerChange(index, 'date_of_birth', e.target.value)}
                                className="w-full px-3 py-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase placeholder-gray-400 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-moz-calendar-picker-indicator]:hidden"
                              />
                              {validationErrors[`date_of_birth_${index}`] && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors[`date_of_birth_${index}`]}</p>
                              )}
                            </div>
                          </>
                        )}

                        {/* Save for Future Bookings - Only for last passenger */}
                        {index === passengers.length - 1 && (
                          <div className="mb-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={saveForFuture}
                                onChange={(e) => setSaveForFuture(e.target.checked)}
                                className="mr-2"
                              />
                              <span className="text-sm">Save for Future Bookings</span>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="mt-4 mb-2 mr-2 flex justify-end gap-2">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-300 text-black text-sm font-normal rounded shadow-none hover:bg-gray-400 transition-colors"
                        onClick={handleGoBack}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#ffd200] text-black text-sm font-normal rounded shadow-none hover:bg-[#e6bb00] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        disabled={submitting}
                      >
                        {submitting ? 'Processing...' : 'Next'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Right Column - Fare Summary */}
                <div className="w-full lg:w-1/3 px-4 mt-6 lg:mt-0">
                  <div className="bg-white border-0 rounded-none overflow-hidden sticky top-24">
                    <div className="bg-[#ffd200] px-4 py-3">
                      <h5 className="text-base font-bold m-0">Fare</h5>
                    </div>
                    <div className="p-4">
                      {/* Multi-city fare breakdown (3+ flights) */}
                      {bookingData.selectedFlights && bookingData.selectedFlights.length > 2 ? (
                        <div className="space-y-4">
                          {bookingData.selectedFlights.map((flight, idx) => {
                            const farePax = flight.seat_price
                              ? Math.round(parseFloat(flight.seat_price) + (parseFloat(flight.seat_price) * (flight.commission_fee || 0) / 100))
                              : parseFloat(flight.block_empty_price || 0);
                            return (
                              <div key={idx}>
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm font-bold text-gray-700">
                                    {flight.departure?.airport_code || 'DEP'}
                                  </span>
                                  <span className="text-gray-400 text-xs">→</span>
                                  <span className="text-sm font-bold text-gray-700">
                                    {flight.arrival?.airport_code || 'ARR'}
                                  </span>
                                </div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Passenger(s)</span>
                                    <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Fare/Pax</span>
                                    <span className="text-gray-700 font-semibold">${formatCurrency(farePax)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Total Fare</span>
                                    <span className="text-gray-700 font-semibold">${formatCurrency(farePax * totalPassengers)}</span>
                                  </div>
                                </div>
                                {idx < bookingData.selectedFlights.length - 1 && (
                                  <div className="border-t-2 border-dashed border-gray-300 mt-3"></div>
                                )}
                              </div>
                            );
                          })}
                          {/* Grand Total */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                              <span className="text-gray-700 font-bold">{totalPassengers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Grand Total</span>
                              <span className="text-gray-700 font-bold">
                                ${formatCurrency(
                                  bookingData.selectedFlights.reduce((sum, flight) => {
                                    const farePax = flight.seat_price
                                      ? Math.round(parseFloat(flight.seat_price) + (parseFloat(flight.seat_price) * (flight.commission_fee || 0) / 100))
                                      : parseFloat(flight.block_empty_price || 0);
                                    return sum + farePax * totalPassengers;
                                  }, 0)
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : bookingData.selectedFlights && bookingData.selectedFlights.length === 2 ? (
                        // Return flight fare breakdown
                        <div className="space-y-4">
                          {/* Outbound Flight */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <span className="text-sm font-bold text-gray-700">
                                {bookingData.selectedFlights[0].departure?.airport_code || 'DEP'}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-sm font-bold text-gray-700">
                                {bookingData.selectedFlights[0].arrival?.airport_code || 'ARR'}
                              </span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Passenger(s)</span>
                                <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Fare/Pax</span>
                                <span className="text-gray-700 font-semibold">
                                  ${formatCurrency(
                                    bookingData.selectedFlights[0].seat_price
                                      ? Math.round(parseFloat(bookingData.selectedFlights[0].seat_price) +
                                        (parseFloat(bookingData.selectedFlights[0].seat_price) * (bookingData.selectedFlights[0].commission_fee || 0) / 100))
                                      : parseFloat(bookingData.selectedFlights[0].block_empty_price || 0)
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Total Fare</span>
                                <span className="text-gray-700 font-semibold">
                                  ${formatCurrency(
                                    (bookingData.selectedFlights[0].seat_price
                                      ? Math.round(parseFloat(bookingData.selectedFlights[0].seat_price) +
                                        (parseFloat(bookingData.selectedFlights[0].seat_price) * (bookingData.selectedFlights[0].commission_fee || 0) / 100))
                                      : parseFloat(bookingData.selectedFlights[0].block_empty_price || 0)) * totalPassengers
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>

                          {/* Return Flight */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <span className="text-sm font-bold text-gray-700">
                                {bookingData.selectedFlights[1].departure?.airport_code || 'DEP'}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="text-sm font-bold text-gray-700">
                                {bookingData.selectedFlights[1].arrival?.airport_code || 'ARR'}
                              </span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Passenger(s)</span>
                                <span className="text-gray-700 font-semibold">{totalPassengers}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Fare/Pax</span>
                                <span className="text-gray-700 font-semibold">
                                  ${formatCurrency(
                                    bookingData.selectedFlights[1].seat_price
                                      ? Math.round(parseFloat(bookingData.selectedFlights[1].seat_price) +
                                        (parseFloat(bookingData.selectedFlights[1].seat_price) * (bookingData.selectedFlights[1].commission_fee || 0) / 100))
                                      : parseFloat(bookingData.selectedFlights[1].block_empty_price || 0)
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Total Fare</span>
                                <span className="text-gray-700 font-semibold">
                                  ${formatCurrency(
                                    (bookingData.selectedFlights[1].seat_price
                                      ? Math.round(parseFloat(bookingData.selectedFlights[1].seat_price) +
                                        (parseFloat(bookingData.selectedFlights[1].seat_price) * (bookingData.selectedFlights[1].commission_fee || 0) / 100))
                                      : parseFloat(bookingData.selectedFlights[1].block_empty_price || 0)) * totalPassengers
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="border-t-2 border-dashed border-gray-300"></div>

                          {/* Grand Total */}
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                              <span className="text-gray-700 font-bold">{totalPassengers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700 font-bold">Grand Total</span>
                              <span className="text-gray-700 font-bold">
                                ${formatCurrency(
                                  ((bookingData.selectedFlights[0].seat_price
                                    ? Math.round(parseFloat(bookingData.selectedFlights[0].seat_price) +
                                      (parseFloat(bookingData.selectedFlights[0].seat_price) * (bookingData.selectedFlights[0].commission_fee || 0) / 100))
                                    : parseFloat(bookingData.selectedFlights[0].block_empty_price || 0)) +
                                    (bookingData.selectedFlights[1].seat_price
                                      ? Math.round(parseFloat(bookingData.selectedFlights[1].seat_price) +
                                        (parseFloat(bookingData.selectedFlights[1].seat_price) * (bookingData.selectedFlights[1].commission_fee || 0) / 100))
                                      : parseFloat(bookingData.selectedFlights[1].block_empty_price || 0))) * totalPassengers
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // One-way flight fare breakdown (original)
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">
                              {flightData?.departure?.airport_code || 'LCB'}
                            </p>
                            <b className="text-sm">
                              {flightData?.arrival?.airport_code || 'TAB'}
                            </b>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">Passengers</p>
                            <b className="text-sm">{totalPassengers}</b>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm mb-0">Fare/Pax</p>
                            <b className="text-sm">
                              $ {formatCurrency(pricingData.farePerPax || pricingData.blockFare)}
                            </b>
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                            <p className="text-sm mb-0 font-bold">Total Fare</p>
                            <b className="text-sm">
                              $ {formatCurrency(pricingData.isSeatPricing ? pricingData.totalFare : pricingData.blockFare)}
                            </b>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddPassengersPage;
